import { jsonOrEmpty, safeJson } from './safeJson';
import { API_URL } from "./api";
import { supabase } from "@/integrations/supabase/client";
import type { FeatureFlag } from "@dallali/shared";

// Wire shape lives in @dallali/shared (single source of truth, shape-matched to
// the Nest FeatureFlagDto). Re-exported so existing consumers can keep importing
// `FeatureFlag` from this service.
export type { FeatureFlag };

/**
 * Feature-flags API client (NestJS + Prisma — `app.feature_flags`).
 *
 * Super-admin reads + toggles authenticate with the logged-in user's Supabase
 * JWT (`Authorization: Bearer <access_token>`), NOT the API key. VITE_API_KEY is
 * baked into the public bundle, so it cannot authorize a toggle — the Nest
 * routes are gated by CompositeAuthGuard + JwtSuperAdminGuard, which require a
 * real super_admin token (the fail-closed guard rejects API-key-only callers).
 * The X-API-Key is still sent (the gateway/CORS layer expects
 * it) but does NOT grant access on these routes. Same shape as services/cms.ts.
 */

const API_KEY = import.meta.env.VITE_API_KEY || "";

async function request<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  if (!API_URL) {
    throw new Error(
      "Feature-flags API unavailable: VITE_API_URL is not configured",
    );
  }
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    // X-API-Key is expected by the gateway but does NOT authorize the toggle —
    // the super-admin JWT below is what the Nest guards check.
    "X-API-Key": API_KEY,
    ...(init?.headers as Record<string, string> | undefined),
  };

  const {
    data: { session },
  } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) {
    throw new Error(
      "Feature-flags admin action requires an authenticated super-admin session",
    );
  }
  headers["Authorization"] = `Bearer ${accessToken}`;

  const res = await fetch(`${API_URL}${path}`, { ...init, headers });
  if (!res.ok) {
    const err = (await jsonOrEmpty(res)) as { message?: string };
    throw new Error(err.message || `Feature-flags request failed (${res.status})`);
  }
  if (res.status === 204) return undefined as T;
  return safeJson(res) as Promise<T>;
}

/** All flags for the super-admin toggle list. */
export function fetchFeatureFlags(): Promise<FeatureFlag[]> {
  return request<FeatureFlag[]>("/feature-flags");
}

/** Toggle a single flag on/off. */
export function toggleFeatureFlag(
  key: string,
  enabled: boolean,
): Promise<FeatureFlag> {
  return request<FeatureFlag>(`/feature-flags/${encodeURIComponent(key)}`, {
    method: "PATCH",
    body: JSON.stringify({ enabled }),
  });
}

/**
 * A flag the anonymous frontend may read (allow-listed server-side, e.g.
 * `kyc_onboarding`). No session needed. Fail-closed: any error — no API URL,
 * network, non-2xx, or no answer within PUBLIC_FLAG_TIMEOUT_MS — reads as OFF,
 * so a hiccup never opens a dark-launched flow nor holds the page on a spinner.
 * It never rejects, so React Query has nothing to retry.
 */
export const PUBLIC_FLAG_TIMEOUT_MS = 5_000;

export async function fetchPublicFlag(key: string): Promise<boolean> {
  if (!API_URL) return false;
  try {
    const res = await fetch(`${API_URL}/public/feature-flags/${encodeURIComponent(key)}`, {
      headers: { "X-API-Key": API_KEY },
      signal: AbortSignal.timeout(PUBLIC_FLAG_TIMEOUT_MS),
    });
    if (!res.ok) return false;
    const body = (await safeJson(res)) as { enabled?: unknown };
    return body.enabled === true;
  } catch {
    return false;
  }
}
